import {
  Flex,
  Stack,
  Box,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanButton } from '../helivan/HelivanButton';
import {
  SurveyStatus,
  SurveyType,
  useGetSurveysQuery,
} from '../../generated/graphql';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RiAddCircleLine } from 'react-icons/ri';
import { HelivanLoading } from '../HelivanLoading';
import { HelivanLabel } from '../helivan/HelivanLabel';
import { FaRegClock } from 'react-icons/fa';
import { formatDateShort } from '../../utils/formatDate';
import { SurveyGroups } from './SurveyGroups';

export const Surveys = () => {
  const [searchParams] = useSearchParams();
  const people = searchParams.get('people');
  const { data, loading } = useGetSurveysQuery();
  const surveys = data?.getSurveys ?? [];
  const navigate = useNavigate();
  const surveysWithin7Days = surveys.filter((survey) => {
    const surveyDate = new Date(survey.createdAt ?? '');
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - surveyDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  });
  const surveysNotWithin7Days = surveys.filter((survey) => {
    const surveyDate = new Date(survey.createdAt ?? '');
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - surveyDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 7;
  });

  return (
    <Stack
      width="full"
      height="full"
      spacing={0}
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
      overflow="hidden"
    >
      <Flex
        width="full"
        px="30px"
        py="16px"
        borderBottomColor={HelivanColor.border.primary}
        bg="white"
        borderTopRadius="12px"
        borderBottomWidth="1px"
        justifyContent="space-between"
        alignItems="center"
      >
        <HelivanText variant="largeSubheader">Surveys</HelivanText>
        <HelivanButton
          size="sm"
          variant="green"
          onClick={() => navigate('/surveys/create-new')}
        >
          <HStack spacing={1}>
            <RiAddCircleLine color="#245B4A" size={16} />
            <HelivanText color="#245B4A" variant="largeBody+">
              Create New
            </HelivanText>
          </HStack>
        </HelivanButton>
      </Flex>

      <Tabs
        variant="soft-rounded"
        colorScheme="blue"
        width="full"
        alignItems="center"
        display="flex"
        flexDirection="column"
        pt={2}
        pb={4}
        flex="1"
        overflow="hidden"
        defaultIndex={people ? 1 : 0}
      >
        <TabList>
          <Tab
            _selected={{
              color: HelivanColor.text.blueDark,
              bg: HelivanColor.background.secondary,
            }}
            color={HelivanColor.text.gray}
          >
            Surveys
          </Tab>
          <Tab
            _selected={{
              color: HelivanColor.text.blueDark,
              bg: HelivanColor.background.secondary,
            }}
          >
            People
          </Tab>
        </TabList>

        <TabPanels flex="1" overflow="hidden" width="full">
          <TabPanel p={0} height="full" overflowY="auto">
            {loading ? (
              <Flex
                width="full"
                height="full"
                justifyContent="center"
                alignItems="center"
              >
                <HelivanLoading />
              </Flex>
            ) : (
              <Stack spacing={0} width="full">
                {surveysWithin7Days.length > 0 && (
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    width="full"
                    py={2}
                    px={6}
                    borderBottomWidth="1px"
                    borderColor={HelivanColor.border.primary}
                    borderTopWidth="1px"
                    bg="#F7F8F9"
                  >
                    <HelivanText variant="largeBody+">This Week</HelivanText>
                  </Flex>
                )}
                {surveysWithin7Days.map((survey) => (
                  <Flex
                    key={survey.id}
                    justifyContent="space-between"
                    alignItems="center"
                    width="full"
                    py={4}
                    px={6}
                    borderBottomWidth="1px"
                    bg="white"
                    cursor="pointer"
                    _hover={{
                      bg: HelivanColor.background.secondary,
                    }}
                    onClick={() => navigate(`/surveys/${survey.id}`)}
                  >
                    <HelivanText variant="largeBody" fontWeightOverride={500}>
                      {survey.title}
                    </HelivanText>
                    <HStack spacing={5}>
                      <HelivanLabel
                        variant={'blue'}
                        label={
                          survey.type === SurveyType.AbTesting
                            ? 'A/B Test'
                            : 'Questionnaire'
                        }
                      />
                      <Flex width="80px" justifyContent="flex-start">
                        <HelivanLabel
                          variant={
                            survey.status === SurveyStatus.PendingGeneration ||
                            survey.status === SurveyStatus.Generating
                              ? 'blue'
                              : survey.status === SurveyStatus.Completed
                                ? 'green'
                                : 'error'
                          }
                          label={
                            survey.status === SurveyStatus.PendingGeneration ||
                            survey.status === SurveyStatus.Generating
                              ? 'In Progress'
                              : survey.status === SurveyStatus.Completed
                                ? 'Completed'
                                : 'Failed'
                          }
                        />
                      </Flex>
                      <Box width="110px" justifyContent="flex-start">
                        <HelivanText variant="smallBody+">
                          {survey?.responses?.length || 0} responses
                        </HelivanText>
                      </Box>
                      <Box width="80px">
                        <HStack spacing={2} justifyContent="flex-start">
                          <FaRegClock
                            size={16}
                            color={HelivanColor.text.blueDark}
                          />
                          <HelivanText variant="smallBody+">
                            {formatDateShort(survey.createdAt)}
                          </HelivanText>
                        </HStack>
                      </Box>
                    </HStack>
                  </Flex>
                ))}
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  width="full"
                  py={2}
                  px={6}
                  borderBottomWidth="1px"
                  borderColor={HelivanColor.border.primary}
                  borderTopWidth="1px"
                  bg="#F7F8F9"
                >
                  <HelivanText variant="largeBody+">This Month</HelivanText>
                </Flex>
                {surveysNotWithin7Days.map((survey) => (
                  <Flex
                    key={survey.id}
                    justifyContent="space-between"
                    alignItems="center"
                    width="full"
                    py={4}
                    px={6}
                    borderBottomWidth="1px"
                    bg="white"
                    cursor="pointer"
                    _hover={{
                      bg: HelivanColor.background.secondary,
                    }}
                    onClick={() => navigate(`/surveys/${survey.id}`)}
                  >
                    <HelivanText variant="largeBody" fontWeightOverride={500}>
                      {survey.title}
                    </HelivanText>
                    <HStack spacing={5}>
                      <HelivanLabel
                        variant={'blue'}
                        label={
                          survey.type === SurveyType.AbTesting
                            ? 'A/B Test'
                            : 'Questionnaire'
                        }
                      />
                      <Flex width="80px" justifyContent="flex-start">
                        <HelivanLabel
                          variant={
                            survey.status === SurveyStatus.PendingGeneration ||
                            survey.status === SurveyStatus.Generating
                              ? 'blue'
                              : survey.status === SurveyStatus.Completed
                                ? 'green'
                                : 'error'
                          }
                          label={
                            survey.status === SurveyStatus.PendingGeneration ||
                            survey.status === SurveyStatus.Generating
                              ? 'In Progress'
                              : survey.status === SurveyStatus.Completed
                                ? 'Completed'
                                : 'Failed'
                          }
                        />
                      </Flex>
                      <Box width="80px" justifyContent="flex-start">
                        <HelivanText variant="smallBody+">
                          {survey?.responses?.length || 0} responses
                        </HelivanText>
                      </Box>
                      <Box width="80px">
                        <HStack spacing={2} justifyContent="flex-start">
                          <FaRegClock
                            size={16}
                            color={HelivanColor.text.blueDark}
                          />
                          <HelivanText variant="smallBody+">
                            {formatDateShort(survey.createdAt)}
                          </HelivanText>
                        </HStack>
                      </Box>
                    </HStack>
                  </Flex>
                ))}
              </Stack>
            )}
          </TabPanel>
          <TabPanel p={0} height="full" overflowY="auto">
            <SurveyGroups />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Stack>
  );
};
